
import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d612hekcm4ll99gupcqg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setShowSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
        setTimeout(() => setShowSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-amber-600 text-sm font-medium tracking-wider uppercase mb-3">Get In Touch</p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Send Us a <strong>Message</strong>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Fill out the form below and we'll get back to you within 24 hours with a free quote for your <strong>painting and decorating</strong> project
          </p>
        </div>

        <form
          id="contact-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-beige-50 p-8 md:p-12 rounded-lg shadow-sm border border-beige-200"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-800 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-beige-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-sm bg-white"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-800 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-beige-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-sm bg-white"
                placeholder="Enter your phone number"
              />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="email" className="block text-sm font-semibold text-gray-800 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-beige-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-sm bg-white"
              placeholder="Enter your email address"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="subject" className="block text-sm font-semibold text-gray-800 mb-2">
              Subject *
            </label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-beige-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-sm cursor-pointer bg-white"
            >
              <option value="">Select a subject</option>
              <option value="Free Quote Request">Free Quote Request</option>
              <option value="Interior Painting">Interior Painting</option>
              <option value="Exterior Painting">Exterior Painting</option>
              <option value="Wallpaper Installation">Wallpaper Installation</option>
              <option value="Commercial Project">Commercial Project</option>
              <option value="General Inquiry">General Inquiry</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="mb-6">
            <label htmlFor="message" className="block text-sm font-semibold text-gray-800 mb-2">
              Your Message *
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              maxLength={500}
              rows={6}
              className="w-full px-4 py-3 border border-beige-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all resize-none text-sm bg-white"
              placeholder="Tell us about your project, including property location, size, and any specific requirements"
            ></textarea>
            <p className="text-xs text-gray-500 mt-1">
              Maximum 500 characters ({formData.message.length}/500)
            </p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full md:w-auto px-10 py-4 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors duration-300 whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>

        {showSuccess && (
          <div className="mt-8 p-6 bg-beige-50 border border-beige-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-checkbox-circle-line text-2xl text-amber-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Message Sent Successfully!</h3>
                <p className="text-sm text-gray-600">
                  Thank you for contacting <strong>Brushour</strong>. We've received your message and will respond within 24 hours. For urgent inquiries, please call us at <a href="tel:07595037441" className="text-amber-600 hover:text-amber-700 font-semibold cursor-pointer">07595037441</a> or message us on <a href="https://wa.me/447595037441" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:text-amber-700 font-semibold cursor-pointer">WhatsApp</a>.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
